import useElement from "backend/hooks/elements/useElement";
import { ICardElementWithChildren } from "backend/model/model";
import PerformanceTrend from "components/PerformanceTrend";
import { PerformanceDonut } from "design/components/charts/PerformanceDonut";
import { t } from "i18next";
import { getTranslatedAttribute } from "qpr-base//tplutils/utils";
import * as conf from "qpr-base/configuration";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import React from "react";
import DomElement from "reportengine/DomElement";
import Portion from "reportengine/Portion";
import Slide from "reportengine/Slide";
import Text from "reportengine/Text";
import TextElement from "reportengine/TextElement";
import LastPeriodsName from "../components/LastPeriodName";
import ReportComments from "../components/ReportComment";
import useYearsLastPeriod from "backend/hooks/model/useYearsLastPeriod";
import { useQuery } from "react-query";
import getValues from "backend/values";

const KPISlide = (
  props: React.PropsWithChildren<{
    item: ICardElementWithChildren;
    translatedScorecardName?: string;
  }>
) => {
  const { item } = props;
  const ctx = useAppContext();
  const { data: parentElement, isLoading: isElementLoading } = useElement(item.parent_element_id);
  const lastPeriod = useYearsLastPeriod(ctx.period || "");

  const { data: lastPeriodValue, isLoading: isValuesLoading } = useQuery(
    `last-period-for-${item.id}-${ctx.period}-${lastPeriod?.name}`,
    async () => {
      return getValues(item.id || "", [lastPeriod!], true, []);
    },
    { enabled: !!lastPeriod }
  );

  if (isElementLoading || isValuesLoading) return <></>;
    
  const translatedName = getTranslatedAttribute(item, "Name", ctx.language);
  const translatedParentName = getTranslatedAttribute(
    parentElement,
    "Name",
    ctx.language
  );
  const kpiSett = conf.getKPIDetailsSlideGaugeSettings(item);

  return (
    <>
      <Slide index={6}>
        <TextElement slideElement="footer">
          {props.translatedScorecardName} ({t("configtypes.KPI")})
        </TextElement>
        <Text slideElement="period">
          <Portion paragraphIndex={0} portionIndex={0}>
            {item.period?.name || ""}
          </Portion>
        </Text>
        <TextElement slideElement="header">{translatedParentName}</TextElement>
        <TextElement slideElement="name">{translatedName}</TextElement>
        <Text slideElement="period_actual_value">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("configtypes." + kpiSett.fields?.[0]?.title)}
          </Portion>
          <Portion portionIndex={2} paragraphIndex={0}>
            {item[kpiSett.fields?.[0]?.attribute as keyof ICardElementWithChildren] }
          </Portion>
        </Text>
        <ReportComments
          item={item}
          slideElement="justification_text"
          justificationDateElement="justification_date"
          justificationOwnerElement="justification_owner"
          justificationOwnerNameElement="justification_owner_name"
        />
        <Text slideElement="period_target_value">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("configtypes." + kpiSett.fields?.[1]?.title)}
          </Portion>
          <Portion portionIndex={2} paragraphIndex={0}>
            {item[kpiSett.fields?.[1]?.attribute as keyof ICardElementWithChildren]}
          </Portion>
        </Text>
        <Text slideElement="target_achievement_title">
          <Portion portionIndex={0} paragraphIndex={0}>
            {t("strategyReport.Target Achievement YTD")}
          </Portion>
          <Portion portionIndex={1} paragraphIndex={0}>
            {` • ${item.period?.name}`}
          </Portion>
        </Text>
        <LastPeriodsName item={item} slideElement="target_title" />
        <TextElement slideElement="performance_title">
          {t("strategyReport.Performance")}
        </TextElement>
        <TextElement slideElement="performance_trend">
          {t("strategyReport.Performance Trend")}
        </TextElement>
        <TextElement slideElement="target_value">
          {lastPeriodValue && lastPeriodValue[0] ? (lastPeriodValue[0][kpiSett.fields?.[2]?.attribute as keyof ICardElementWithChildren] ) : "-"}
        </TextElement>
        <DomElement
          typename="image"
          selector=".element"
          slideElement="target_achievement_donut"
          transform={{
            x: -20,
            width: 240,
            height: 240,
          }}
        >
          <div className="element" style={{ width: "300px", height: "300px" }}>
            <PerformanceDonut
              element={item}
              settings={conf.getKPIDetailsViewGaugeSettings(item)}
              disableAnimation
            />
          </div>
        </DomElement>

        <DomElement
          typename="image"
          selector=".highcharts-root"
          slideElement="performance_trend_chart"
          transform={{
            y: 70,
            height: -20,
            width: -30,
          }}
        >
          <div style={{ width: "1090px", height: "300px" }}>
            <PerformanceTrend
              disableAnimation
              width="1090px"
              element={props.item}
              settings={conf.getKPIDetailsViewChartSettings(props.item)}
              showLegend={true}
            />
          </div>
        </DomElement>
      </Slide>
    </>
  );
};

export default KPISlide;
